import type { components } from '../types/open-api-schema';

export type ExistingLocationFilter =
    | (components['schemas']['Filter.PoolRequestList'] & { address?: string | null })
    | null;

export abstract class LocationFilterBase {
    protected readonly searchBar: HTMLInputElement;
    protected readonly radiusRangeSlider: HTMLInputElement;
    protected readonly latitudeHidden: HTMLInputElement;
    protected readonly longitudeHidden: HTMLInputElement;
    protected readonly addressHidden: HTMLInputElement;
    protected readonly radiusHidden: HTMLInputElement;

    protected constructor(
        protected readonly form: HTMLFormElement,
        protected readonly existingFilter: ExistingLocationFilter
    ) {
        const searchBar = form.querySelector<HTMLInputElement>('#filter-location-address-search');
        const radiusSlider = form.querySelector<HTMLInputElement>('#radius');
        const latitudeHidden = form.querySelector<HTMLInputElement>('#latitude');
        const longitudeHidden = form.querySelector<HTMLInputElement>('#longitude');
        const radiusHidden = form.querySelector<HTMLInputElement>('#locationRange');
        const addressHidden = form.querySelector<HTMLInputElement>('#address');

        if (
            !searchBar ||
            !radiusSlider ||
            !latitudeHidden ||
            !longitudeHidden ||
            !radiusHidden ||
            !addressHidden
        ) {
            throw new Error('LocationFilterBase: required form elements not found');
        }

        this.searchBar = searchBar;
        this.radiusRangeSlider = radiusSlider;
        this.latitudeHidden = latitudeHidden;
        this.longitudeHidden = longitudeHidden;
        this.radiusHidden = radiusHidden;
        this.addressHidden = addressHidden;

        // Restore hidden fields from existing filter (core bug fix)
        const loc = existingFilter?.locationRange?.location;
        if (loc?.latitude != null) {
            this.latitudeHidden.value = String(loc.latitude);
        }
        if (loc?.longitude != null) {
            this.longitudeHidden.value = String(loc.longitude);
        }
        if (existingFilter?.address) {
            this.addressHidden.value = existingFilter.address;
            this.searchBar.value = existingFilter.address;
        }

        // Wire radius slider
        this.radiusRangeSlider.addEventListener('input', this.handleRadiusChange);
        this.radiusRangeSlider.addEventListener('change', this.handleRadiusChange);
        this.syncRadiusLabel();
    }

    protected handleRadiusChange = (event: Event) => {
        this.syncRadiusLabel();
        if (event.type === 'change') {
            this.onRadiusCommit(this.radiusRangeSlider.valueAsNumber);
        }
    };

    private syncRadiusLabel(): void {
        const value = this.radiusRangeSlider.valueAsNumber;
        this.radiusHidden.value = this.radiusRangeSlider.value;
        const labelId = this.radiusRangeSlider.dataset.labelledBy;
        if (labelId) {
            const label = document.querySelector<HTMLElement>(`#${labelId}`);
            if (label) {
                label.innerText = `${value / 1000} km`;
            }
        }
    }

    // Override in subclasses to react to committed radius changes
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected onRadiusCommit(value: number): void {}

    protected clearSearch(): void {
        this.searchBar.value = '';
        this.latitudeHidden.value = '';
        this.longitudeHidden.value = '';
        this.addressHidden.value = '';
    }
}
